import { useEffect, useState, useMemo, Fragment } from "react";
import { useParams, Link } from "react-router-dom";
import { getOrganizationComparison, fmt } from "../lib/api";
import { Building2, ArrowLeft, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { Button } from "../components/ui/button";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  LineChart, Line, Legend, CartesianGrid,
} from "recharts";

const KATEGORILER = [
  { key: "k1", label: "K1 — Doğrudan Emisyonlar" },
  { key: "k2", label: "K2 — Satın Alınan Enerji" },
  { key: "k3", label: "K3 — Ulaşım" },
  { key: "k4", label: "K4 — Dolaylı (Ürün Kullanımı)" },
  { key: "k5", label: "K5 — Satılan Ürünler" },
  { key: "k6", label: "K6 — Diğer" },
];

function ChangeBadge({ from, to }) {
  if (from === undefined || from === null || from === 0) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }
  const diff = to - from;
  const pct = (diff / from) * 100;
  const sign = diff > 0 ? "+" : "";
  const color = diff > 0 ? "text-red-600" : diff < 0 ? "text-emerald-600" : "text-muted-foreground";
  const Icon = diff > 0 ? TrendingUp : diff < 0 ? TrendingDown : Minus;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium ${color}`}>
      <Icon className="size-3" />
      {sign}{fmt(pct, 1)}%
    </span>
  );
}

export default function OrganizationComparison() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    getOrganizationComparison(id)
      .then(setData)
      .catch((e) => setError(e?.response?.data?.detail || "Karşılaştırma yüklenemedi"))
      .finally(() => setLoading(false));
  }, [id]);

  const chartData = useMemo(() => {
    if (!data?.years) return [];
    return data.years.map((y) => ({
      year: String(y.year),
      Toplam: Number(y.total?.toFixed?.(3) ?? y.total ?? 0),
      K1: Number(y.k1?.toFixed?.(3) ?? 0),
      K2: Number(y.k2?.toFixed?.(3) ?? 0),
      K3: Number(y.k3?.toFixed?.(3) ?? 0),
      K4: Number(y.k4?.toFixed?.(3) ?? 0),
      K5: Number(y.k5?.toFixed?.(3) ?? 0),
      K6: Number(y.k6?.toFixed?.(3) ?? 0),
    }));
  }, [data]);

  if (loading) return <div className="px-6 md:px-14 py-14 text-muted-foreground">Yükleniyor…</div>;
  if (error) return (
    <div className="px-6 md:px-14 py-14">
      <div className="rounded-2xl border border-destructive/30 bg-destructive/5 p-6 text-destructive" data-testid="comparison-error">{error}</div>
    </div>
  );
  if (!data) return null;

  const { organization, years } = data;

  return (
    <div className="px-6 md:px-14 py-10 md:py-14 max-w-7xl" data-testid="comparison-page">
      <div className="mb-6">
        <Link to="/organizations" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition">
          <ArrowLeft className="size-4" /> Tesislere Dön
        </Link>
      </div>

      <div className="flex items-center gap-4 mb-10">
        <div className="size-16 rounded-2xl bg-primary/10 flex items-center justify-center overflow-hidden">
          {organization.logo_base64 ? (
            <img src={organization.logo_base64} alt="" className="object-contain w-full h-full" />
          ) : (
            <Building2 className="size-7 text-primary" />
          )}
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-2">Yıllık Karşılaştırma</div>
          <h1 className="font-display text-4xl sm:text-5xl font-semibold">{organization.name}</h1>
        </div>
      </div>

      {years.length === 0 ? (
        <div className="rounded-2xl border border-dashed p-14 text-center">
          <div className="font-display text-xl mb-1">Henüz rapor bulunmuyor</div>
          <div className="text-sm text-muted-foreground mb-5">Bu tesis için en az bir rapor oluşturun.</div>
          <Link to={`/reports/new?org=${organization.id}`}>
            <Button className="rounded-full">Rapor Oluştur</Button>
          </Link>
        </div>
      ) : years.length === 1 ? (
        <div className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-8" data-testid="single-year-notice">
          <div className="font-display text-xl mb-2">Tek Yıl Verisi Mevcut</div>
          <div className="text-sm text-muted-foreground mb-4">
            Karşılaştırma için en az 2 farklı yılın raporu gereklidir. Şu anda yalnızca <b>{years[0].year}</b> yılı raporu bulunmaktadır.
          </div>
          <Link to={`/reports/new?org=${organization.id}`}>
            <Button className="rounded-full">Yeni Yıl İçin Rapor Ekle</Button>
          </Link>
        </div>
      ) : (
        <>
          {/* KPI Cards — son 2 yıl */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
            {(() => {
              const last = years[years.length - 1];
              const prev = years[years.length - 2];
              return [
                { label: `${last.year} Toplam Emisyon`, value: last.total, unit: "tCO₂e", prev: prev.total },
                { label: `${last.year} Kapsam 1+2`, value: last.kapsam1 + last.kapsam2, unit: "tCO₂e", prev: prev.kapsam1 + prev.kapsam2 },
                { label: `${last.year} Kişi Başına`, value: last.kisi_basina, unit: "tCO₂e/oda-gece", prev: prev.kisi_basina },
              ].map((c) => (
                <div key={c.label} className="p-6 rounded-2xl border border-border bg-card">
                  <div className="text-xs text-muted-foreground uppercase tracking-wider mb-2">{c.label}</div>
                  <div className="font-display text-3xl font-semibold">{fmt(c.value, 3)}</div>
                  <div className="text-xs text-muted-foreground mt-1">{c.unit}</div>
                  <div className="mt-3">
                    <ChangeBadge from={c.prev} to={c.value} />
                    <span className="text-xs text-muted-foreground ml-2">önceki yıla göre</span>
                  </div>
                </div>
              ));
            })()}
          </div>

          {/* Bar chart — toplam emisyon */}
          <div className="p-6 rounded-2xl border border-border bg-card mb-6">
            <h3 className="font-display text-xl mb-4">Yıllara Göre Toplam Emisyon (tCO₂e)</h3>
            <div className="h-64">
              <ResponsiveContainer>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="year" tick={{ fill: "hsl(var(--foreground))", fontSize: 12 }} />
                  <YAxis tick={{ fill: "hsl(var(--foreground))", fontSize: 12 }} />
                  <Tooltip
                    contentStyle={{
                      background: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: 12,
                    }}
                    formatter={(v) => [`${fmt(v, 3)} tCO₂e`, "Toplam"]}
                  />
                  <Bar dataKey="Toplam" fill="hsl(var(--primary))" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Line chart — kategori bazında trend */}
          <div className="p-6 rounded-2xl border border-border bg-card mb-6">
            <h3 className="font-display text-xl mb-4">Kategorilere Göre Yıllık Trend</h3>
            <div className="h-72">
              <ResponsiveContainer>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="year" tick={{ fill: "hsl(var(--foreground))", fontSize: 12 }} />
                  <YAxis tick={{ fill: "hsl(var(--foreground))", fontSize: 12 }} />
                  <Tooltip
                    contentStyle={{
                      background: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: 12,
                    }}
                    formatter={(v) => `${fmt(v, 3)} tCO₂e`}
                  />
                  <Legend />
                  <Line type="monotone" dataKey="K1" stroke="#ef4444" strokeWidth={2} />
                  <Line type="monotone" dataKey="K2" stroke="#f59e0b" strokeWidth={2} />
                  <Line type="monotone" dataKey="K3" stroke="#10b981" strokeWidth={2} />
                  <Line type="monotone" dataKey="K4" stroke="#3b82f6" strokeWidth={2} />
                  <Line type="monotone" dataKey="K5" stroke="#8b5cf6" strokeWidth={2} />
                  <Line type="monotone" dataKey="K6" stroke="#ec4899" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Detaylı tablo */}
          <div className="p-6 rounded-2xl border border-border bg-card overflow-x-auto" data-testid="comparison-table">
            <h3 className="font-display text-xl mb-4">Detaylı Yıllık Tablo (tCO₂e)</h3>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 px-2 font-medium text-muted-foreground">Kategori</th>
                  {years.map((y) => (
                    <th key={y.year} className="text-right py-3 px-2 font-medium" colSpan={2}>{y.year}</th>
                  ))}
                </tr>
                <tr className="border-b border-border text-xs text-muted-foreground">
                  <th></th>
                  {years.map((y, i) => (
                    <Fragment key={`h-${y.year}`}>
                      <th className="text-right py-2 px-2 font-normal">Değer</th>
                      <th className="text-right py-2 px-2 font-normal">{i === 0 ? "—" : "Δ %"}</th>
                    </Fragment>
                  ))}
                </tr>
              </thead>
              <tbody>
                {KATEGORILER.map((cat) => (
                  <tr key={cat.key} className="border-b border-border/50 hover:bg-muted/30">
                    <td className="py-3 px-2 font-medium">{cat.label}</td>
                    {years.map((y, i) => (
                      <Fragment key={`${cat.key}-${y.year}`}>
                        <td className="text-right py-3 px-2 tabular-nums">
                          {fmt(y[cat.key], 3)}
                        </td>
                        <td className="text-right py-3 px-2">
                          {i === 0 ? <span className="text-xs text-muted-foreground">—</span> : (
                            <ChangeBadge from={years[i - 1][cat.key]} to={y[cat.key]} />
                          )}
                        </td>
                      </Fragment>
                    ))}
                  </tr>
                ))}

                <tr className="border-b-2 border-border bg-muted/30">
                  <td className="py-3 px-2 font-display font-semibold">TOPLAM</td>
                  {years.map((y, i) => (
                    <Fragment key={`t-${y.year}`}>
                      <td className="text-right py-3 px-2 tabular-nums font-semibold">
                        {fmt(y.total, 3)}
                      </td>
                      <td className="text-right py-3 px-2">
                        {i === 0 ? <span className="text-xs text-muted-foreground">—</span> : (
                          <ChangeBadge from={years[i - 1].total} to={y.total} />
                        )}
                      </td>
                    </Fragment>
                  ))}
                </tr>

                <tr className="text-muted-foreground">
                  <td className="py-3 px-2 italic">Kişi Başına (tCO₂e/oda-gece)</td>
                  {years.map((y, i) => (
                    <Fragment key={`k-${y.year}`}>
                      <td className="text-right py-3 px-2 tabular-nums">
                        {fmt(y.kisi_basina, 4)}
                      </td>
                      <td className="text-right py-3 px-2">
                        {i === 0 ? <span className="text-xs">—</span> : (
                          <ChangeBadge from={years[i - 1].kisi_basina} to={y.kisi_basina} />
                        )}
                      </td>
                    </Fragment>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
